import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as client from "../../../client";
import {
  createBorLetter,
  getBorLettersByReport,
  listBorLetters,
  markBorStep,
} from "../../../api/routes";

// ── Query Keys ──

const borKeys = {
  all: ["bor-letters"] as const,
  byReport: (reportId: string) =>
    ["bor-letters", "by-report", reportId] as const,
  list: (activeOnly: boolean) => ["bor-letters", "list", activeOnly] as const,
};

// ── Queries ──

export const useBorLettersByReport = (reportId: string | undefined) =>
  useQuery({
    enabled: !!reportId,
    queryKey: borKeys.byReport(reportId ?? ""),
    queryFn: () => getBorLettersByReport(reportId!),
    staleTime: 30_000,
  });

export const useBorLetters = (activeOnly: boolean = false) =>
  useQuery({
    queryKey: borKeys.list(activeOnly),
    queryFn: () => listBorLetters(activeOnly),
    staleTime: 30_000,
  });

/** Returns the first active (unresolved) BOR for a report, if any. */
export const useActiveBorForReport = (reportId: string | undefined) => {
  const query = useBorLettersByReport(reportId);
  const activeBor = query.data?.find((b) => !b.is_resolved) ?? null;
  return { ...query, data: activeBor };
};

// ── Mutations ──

export const useCreateBorLetter = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: client.CreateBorLetterRequest) =>
      createBorLetter(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: borKeys.all });
    },
  });
};

export const useMarkBorStep = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      borId,
      step,
    }: {
      borId: string;
      step: client.MarkBorStepRequest["step"];
    }) => markBorStep(borId, step),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: borKeys.all });
    },
  });
};

/** Returns true when a BOR is resolved AND all notification steps are done. */
export const isBorFullyComplete = (bor: client.BorLetterResponse): boolean =>
  bor.is_resolved &&
  bor.outgoing_broker_notified_at !== null &&
  bor.incoming_broker_notified_at !== null &&
  bor.outgoing_broker_resolution_sent_at !== null &&
  bor.incoming_broker_resolution_sent_at !== null;

/** Returns the most relevant BOR for badge display (not yet fully complete).
 *  Returns null only when every BOR for this report is fully complete. */
export const useBorForReportBadge = (reportId: string | undefined) => {
  const query = useBorLettersByReport(reportId);
  const bor = query.data?.find((b) => !isBorFullyComplete(b)) ?? null;
  return { ...query, data: bor };
};

// ── Helpers ──

export type BorStepInfo = {
  key: client.MarkBorStepRequest["step"];
  label: string;
  timestamp: string | null;
};

export const BOR_STEPS: BorStepInfo["key"][] = [
  "outgoing_broker_notified_at",
  "incoming_broker_notified_at",
  "rescind_letter_received_at",
  "finalized_at",
  "outgoing_broker_resolution_sent_at",
  "incoming_broker_resolution_sent_at",
];

export const borStepLabel = (step: BorStepInfo["key"]): string => {
  switch (step) {
    case "outgoing_broker_notified_at":
      return "Outgoing broker notified";
    case "incoming_broker_notified_at":
      return "Incoming broker acknowledged";
    case "rescind_letter_received_at":
      return "Rescind letter received";
    case "finalized_at":
      return "BOR finalized";
    case "outgoing_broker_resolution_sent_at":
      return "Resolution sent to outgoing broker";
    case "incoming_broker_resolution_sent_at":
      return "Resolution sent to incoming broker";
  }
};

export const getBorSteps = (bor: client.BorLetterResponse): BorStepInfo[] =>
  BOR_STEPS.map((key) => ({
    key,
    label: borStepLabel(key),
    timestamp: bor[key],
  }));

export const borSideForReport = (
  bor: client.BorLetterResponse,
  reportId: string
): "cleared" | "incoming" => {
  return bor.cleared_report_id === reportId ? "cleared" : "incoming";
};

export const borOtherReportId = (
  bor: client.BorLetterResponse,
  reportId: string
): string => {
  return bor.cleared_report_id === reportId
    ? bor.incoming_report_id
    : bor.cleared_report_id;
};
